import { EditorMode } from "shared/enums/EditorModeEnum";
import { ValidationGroup } from "devextreme-react/validation-group";
import React from "react";
import { AppDispatch, RootState } from "store/store";
import { connect } from "react-redux";
import EditorActionFooter from "components/common/EditorActionFooter";
import FormGroup from "components/common/FormGroup";
import Checkbox from "components/common/controls/Checkbox";
import InputField from "components/common/controls/InputField";
import { Component } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import Select from "components/common/controls/Select";
import EditorModal from "../EditorModal";
import LicenseRequest from "interfaces/request/LicenseRequest";
import AdminProductLicense from "interfaces/response/AdminProductLicense";
import DropdownValuesResponse from "interfaces/response/DropdownValuesResponse";
import PromineoNumberBox from "components/common/controls/PromineoNumberBox";
import { loadLicenseRelatedDropdownData, createNewProductLicense, updateProductLicense } from "store/slices/ProductLicenseManagementSlice";
import AdminProduct from "interfaces/response/AdminProduct";
import { DefaultBillingIntervalValue, DefaultActionValidationDurationUnitValue } from "shared/constants/DropdownConstantValues";

interface Props {
  license?: AdminProductLicense;
  dispatch?: AppDispatch;
  selectedProduct?: AdminProduct | null;
  mode: EditorMode;
  billingIntervalValues?: DropdownValuesResponse[];
  actionValidationDurationUnitValues?: DropdownValuesResponse[];
  onClose: () => void;
}

interface State {
  isLoading: boolean;
  licenseRequest: LicenseRequest;
}

export class LicenseEditorPopup extends Component<Props, State> {
  isInEditMode = this.props.mode === EditorMode.Edit;
  licenseId = this.props.license ? this.props.license.id : -1; // For New License we are considering licenseId = -1 //
  modalTitle = this.isInEditMode ? "Edit License" : "Create New License";
  applyButtonCaption = this.isInEditMode ? "SAVE CHANGES" : "Create New";
  cancelButtonCaption = "Cancel";
  
  isCustomizableLicenseFieldName = "isCustomizableLicense";
  isAutomaticPurchaseAllowedFieldName = "isAutomaticPurchaseAllowed";
  isDefaultFreeLicenseFieldName = "isDefaultFreeLocalLicense";
  priceFieldName = "price";
  numberOfAllowedActionsFieldName = "numberOfAllowedActions";
  billingIntervalFieldName = "billingInterval";
  actionValidationDurationUnitFieldName = "actionValidationDurationUnit";

  formValidationGroupRef = React.createRef<any>();

  state: Readonly<State> = {
    isLoading: false,
    licenseRequest: {
      productId: this.props.selectedProduct!.id, // We will always have a selected product //
      name: this.props.license ? this.props.license.name : "",
      isCustomizableLicense: this.props.license ? this.props.license.isCustomizableLicense : false, // Assuming default value is false
      tag: this.props.license ? this.props.license.tag : "",
      isAutomaticPurchaseAllowed: this.props.license ? this.props.license.isAutomaticPurchaseAllowed : false, // Assuming default value is false
      numberOfAllowedActions: this.props.license ? this.props.license.numberOfAllowedActions : 0,
      price: this.props.license?.price,
      billingInterval: this.props.license ? this.props.license.billingInterval : DefaultBillingIntervalValue,
      isDefaultFreeLocalLicense: this.props.license ? this.props.license.isDefaultFreeLocalLicense : false,
      stripeId: this.props.license ? this.props.license.stripeId : "",
      actionValidationDurationUnit: this.props.license ? this.props.license.actionValidationDurationUnit : DefaultActionValidationDurationUnitValue
    }
  }

  componentDidMount() {
    // Loading Dropdown values if they are not loaded
    if (this.props.billingIntervalValues?.length === 0 || this.props.actionValidationDurationUnitValues?.length === 0) {
      this.setState({ isLoading: true });
      this.props.dispatch!(loadLicenseRelatedDropdownData())
        .then(() => {
          this.setState({ isLoading: false });
        })
    }
  }

  handleChange = (value: any, fieldName: string) => {
    this.setState({ 
      licenseRequest: {
        ...this.state.licenseRequest,
        [fieldName]: value
      }
    });
  }

  handleSubmit = () => {
    var validationResult = this.formValidationGroupRef.current.instance.validate();
    if (!validationResult.isValid) {
      return;
    }

    const { 
      productId, 
      name, 
      isCustomizableLicense, 
      tag, 
      isAutomaticPurchaseAllowed, 
      numberOfAllowedActions, 
      price, 
      billingInterval, 
      isDefaultFreeLocalLicense, 
      stripeId, 
      actionValidationDurationUnit } = this.state.licenseRequest;

    const licenseDetails: LicenseRequest = {
      productId, 
      name, 
      isCustomizableLicense, 
      tag, 
      isAutomaticPurchaseAllowed, 
      numberOfAllowedActions, 
      price, 
      billingInterval, 
      isDefaultFreeLocalLicense, 
      stripeId, 
      actionValidationDurationUnit
    }

    this.setState({ isLoading: true });
    if (this.isInEditMode) {
      this.props.dispatch!(
        updateProductLicense({
          licenseId: this.licenseId,
          licenseRequest: licenseDetails,
        })
      )
      .then(unwrapResult)
      .then(() => {
        this.props.onClose?.();
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
    }
    else {
      this.props.dispatch!(createNewProductLicense(licenseDetails))
      .then(unwrapResult)
      .then(() => {
        this.props.onClose?.();
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
    }
  };

  render() {
    return (
      <>
        <EditorModal title={this.modalTitle} onClose={() => this.props.onClose?.()} isLoading={this.state.isLoading}>
          <div>
            <form>
              <ValidationGroup ref={this.formValidationGroupRef}>
                <div className="grid gap-x-3 gap-y-4 grid-cols-2">
                  <div className="col-span-2">
                    <FormGroup label="Name">
                      <InputField
                        placeholder="Name"
                        id="name"
                        label="Name"
                        name="name"
                        defaultValue={this.state.licenseRequest.name}
                        required={true}
                        onChange={(e : any) => {
                          this.handleChange(e.event.target.value, e.event.target.name);
                        }}
                      />
                    </FormGroup>
                  </div>
                  <FormGroup label="Tag">
                    <InputField
                      placeholder="Tag"
                      id="tag"
                      label="Tag"
                      name="tag"
                      defaultValue={this.state.licenseRequest.tag}
                      onChange={(e : any) => {
                        this.handleChange(e.event.target.value, e.event.target.name);
                      }}
                    />
                  </FormGroup>
                  <FormGroup label="Stripe Id">
                    <InputField
                      placeholder="Stripe Id"
                      id="stripeId"
                      label="Stripe Id"
                      name="stripeId"
                      maxLength={255}
                      defaultValue={this.state.licenseRequest.stripeId}
                      onChange={(e : any) => {
                        this.handleChange(e.event.target.value, e.event.target.name);
                      }}
                    />
                  </FormGroup>
                  <FormGroup label="Price">
                    <PromineoNumberBox
                      placeholder="Price"
                      id="price"
                      label="Price"
                      name="price"
                      min={0}
                      defaultValue={this.state.licenseRequest.price}
                      onChange={(e : any) => {
                        this.handleChange(e.event.target.value, this.priceFieldName);
                      }}
                    />
                  </FormGroup>
                  <FormGroup label="Action Numbers">
                    <PromineoNumberBox
                      placeholder="Action numbers"
                      id="numberOfAllowedActions"
                      label="Number Of Allowed Actions"
                      name="numberOfAllowedActions"
                      min={0}
                      format= "#"
                      defaultValue={this.state.licenseRequest.numberOfAllowedActions}
                      onChange={(e : any) => {
                        this.handleChange(e.event.target.value, this.numberOfAllowedActionsFieldName);
                      }}
                    />
                  </FormGroup>
                  <FormGroup label="Billing Interval">
                    <Select
                      dataSource={this.props.billingIntervalValues}
                      required={true}
                      className="w-30"
                      defaultValue={this.state.licenseRequest.billingInterval}
                      displayExpr="name"
                      valueExpr="value"
                      onValueChange={(e) => {
                        this.handleChange(e, this.billingIntervalFieldName);
                      }}
                    />
                  </FormGroup>
                  <FormGroup label="Action Validation">
                    <Select
                      dataSource={this.props.actionValidationDurationUnitValues}
                      required={true}
                      className="w-30"
                      defaultValue={this.state.licenseRequest.actionValidationDurationUnit}
                      displayExpr="name"
                      valueExpr="value"
                      onValueChange={(e) => {
                        this.handleChange(e, this.actionValidationDurationUnitFieldName);
                      }}
                    />
                  </FormGroup>
                  <div className="col-span-2">
                    <FormGroup label="Customizable License">
                      <Checkbox
                        className="w-28"
                        name="isCustomizableLicense"
                        text="Yes"
                        label="Customizable License"
                        defaultValue={this.state.licenseRequest.isCustomizableLicense}
                        onValueChange={(e : any) => {
                          this.handleChange(e, this.isCustomizableLicenseFieldName);
                        }}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-span-2">
                    <FormGroup label="Automatic Purchase Allowed">
                      <Checkbox
                        className="w-28"
                        name="isAutomaticPurchaseAllowed"
                        text="Yes"
                        label="automaticPurchaseAllowed"
                        defaultValue={this.state.licenseRequest.isAutomaticPurchaseAllowed}
                        onValueChange={(e : any) => {
                          this.handleChange(e, this.isAutomaticPurchaseAllowedFieldName);
                        }}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-span-2">
                    <FormGroup label="Default Free License">
                      <Checkbox
                        className="w-28"
                        name="isDefaultFreeLicense"
                        text="Yes"
                        label="isDefaultFreeLicense"
                        defaultValue={this.state.licenseRequest.isDefaultFreeLocalLicense}
                        onValueChange={(e : any) => {
                          this.handleChange(e, this.isDefaultFreeLicenseFieldName);
                        }}
                      />
                    </FormGroup>
                  </div>
                </div>
              </ValidationGroup>
              <div className="mt-6">
                <EditorActionFooter 
                  applyButtonCaption={this.applyButtonCaption}
                  cancelButtonCaption={this.cancelButtonCaption}
                  onApply={this.handleSubmit}
                  onCancel={() => this.props.onClose?.()}
                />
              </div>
            </form>
          </div>
        </EditorModal>
      </>
    );
  }
}


const mapStateToProps = (state: RootState, props: Props) => {
  return {
    ...props,
    billingIntervalValues: state.dropDownData.billingIntervalValues,
    actionValidationDurationUnitValues: state.dropDownData.actionValidationDurationUnitValues,
    selectedProduct: state.productManagementData.selectedProduct,
  };
};

export default connect(mapStateToProps)(LicenseEditorPopup);
